import { supabase } from '../../config/supabase';
import { Database } from '@travel-dashboard/shared';

export type BudgetCategory = Database['travel']['Tables']['budget_categories']['Row'];

export interface CreateBudgetCategoryInput {
  trip_id: string;
  category_name: string;
  allocated_amount: number;
  currency?: string;
}

export interface UpdateBudgetCategoryInput {
  category_name?: string;
  allocated_amount?: number;
  currency?: string;
}

export const budgetsService = {
  /**
   * Get all budget categories for a trip
   */
  async getTripBudgetCategories(tripId: string): Promise<BudgetCategory[]> {
    const { data, error } = await supabase
      .from('budget_categories')
      .select('*')
      .eq('trip_id', tripId)
      .order('category_name');

    if (error) {
      console.error('Error fetching budget categories:', error);
      throw error;
    }

    return data || [];
  },

  /**
   * Create a new budget category
   */
  async createBudgetCategory(input: CreateBudgetCategoryInput): Promise<BudgetCategory> {
    const { data, error } = await supabase
      .from('budget_categories')
      .insert(input)
      .select()
      .single();

    if (error) {
      console.error('Error creating budget category:', error);
      throw error;
    }

    return data;
  },

  /**
   * Update a budget category
   */
  async updateBudgetCategory(
    categoryId: string,
    input: UpdateBudgetCategoryInput
  ): Promise<BudgetCategory> {
    const { data, error } = await supabase
      .from('budget_categories')
      .update(input)
      .eq('id', categoryId)
      .select()
      .single();

    if (error) {
      console.error('Error updating budget category:', error);
      throw error;
    }

    return data;
  },

  /**
   * Delete a budget category
   */
  async deleteBudgetCategory(categoryId: string): Promise<void> {
    const { error } = await supabase
      .from('budget_categories')
      .delete()
      .eq('id', categoryId);

    if (error) {
      console.error('Error deleting budget category:', error);
      throw error;
    }
  },

  /**
   * Bulk upsert budget categories for a trip
   */
  async upsertBudgetCategories(
    tripId: string,
    categories: Array<{ category_name: string; allocated_amount: number; currency?: string }>
  ): Promise<BudgetCategory[]> {
    const { data, error } = await supabase
      .from('budget_categories')
      .upsert(
        categories.map((cat) => ({
          trip_id: tripId,
          ...cat,
        })),
        {
          onConflict: 'trip_id,category_name',
        }
      )
      .select();

    if (error) {
      console.error('Error upserting budget categories:', error);
      throw error;
    }

    return data || [];
  },
};
